/**
 * Component for rendering a view of a reseller's commision program within a vendor partner program
 * commission block.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment'; 
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Grid, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';

import ServerError from '../ui/ServerError';
import LoadingSpinner from '../ui/LoadingSpinner';

const abEnvironment = new AbEnvironment();
const RcpViewQuery = graphql`
query RcpViewQuery($resellerPId: UUID!) {
  resellerProgramTByResellerPId(resellerPId: $resellerPId) {
    resellerPId
    resellerAccountId
    programType
    programName
    description
    notes
    qualificationPeriod
    autoPayout
    active
    resellerProgramLineTsByResellerPId(orderBy:REVENUE_MIN_ASC) {
      totalCount
      nodes {
        resellerPlId
        revenuePeriod
        revenueMin
        incentivePerc
      }
    }
  }
}
 `;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
    },
    flex: {
      flex: 1,
    },
    containerSize: {
      // width: '60%',
       //minWidth: 250
    },
    tblHead: {
      backgroundColor: '#f1f1f1'
    }
  });
  
class RcpView extends Component {
  state = {
    apps: null
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  goBack() {
    this.props.history.goBack();
  }

  render() {
    const { isAuthenticated, isAuthenticatedReseller, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticated() || needAssent()) {
      return (
        <Redirect to='/home' />
      );
    } else if (isAuthenticatedReseller()) {

      let resellerPId = this.props.resellerPId;

      if (resellerPId == null) {
        // return an empty program.  Shouldn't be called.
        return (
          <Grid container className={classes.containerSize} spacing={0} direction="column" justifyContent="flex-start">
            <Grid item>
              <Typography variant="body2">You aren't signed up for a commission program.</Typography>
            </Grid>
          </Grid>
        );
      }
      return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={RcpViewQuery}
          variables={{resellerPId: resellerPId}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              let program = props.resellerProgramTByResellerPId;
              let programLines = program.resellerProgramLineTsByResellerPId.nodes;
              return (
                <Grid container className={classes.containerSize} spacing={2} direction="row" justifyContent="space-between">
                  {this.props.nameInHeading === true &&
                  <Grid item xs={12}>
                    <Grid container spacing={0}>
                      <Grid item xs={12}>
                        <Typography variant="h6">{program.programName}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2">{program.description}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>}
                  {this.props.nameInHeading === false && 
                  <Grid item xs={12}>
                    <Typography variant="body2">{program.programName} - {program.description}</Typography>
                  </Grid>}
                  <Grid item xs={12}>
                    <Grid container spacing={0} direction="column" justifyContent="flex-start">
                      <Grid item xs={12}> 
                      {program.programType === 0 &&
                        <Typography variant="body2">                                                          
                          <b>Instant rebate</b>
                        </Typography>
                      }
                      {program.programType === 1 &&
                        <Typography variant="body2">                                                             
                          <b>Monthly rebate</b>
                        </Typography>       
                      }
                      {program.programType === 2 &&
                        <Typography variant="body2">          
                          Monthly credits
                        </Typography>
                      }
                      {program.programType === 3 &&
                        <Typography variant="body2">                           
                          Prepaid credits
                        </Typography>
                      }
                      {program.programType === 4 &&
                        <Typography variant="body2">                           
                          Referral
                        </Typography>
                      }
                      </Grid>
                      <Grid item xs={12}>
                      {program.programType === 0 &&
                        <Typography variant="body2">                            
                          Wholesale discounts on subscriptions purchased through AppBind are applied immediately on every charge.
                        </Typography>
                      }
                      {program.programType === 1 &&
                        <Typography variant="body2">                            
                          At the end of the month, a sales commission is calculated based on volume during the month, and paid out as a rebate.
                        </Typography>
                      }
                      {program.programType === 2 &&
                        <Typography variant="body2">                            
                        At the end of the month, a sales commission is calculated based on volume during the month, and paid out as credits that can be applied to future purchases.
                        </Typography>
                      }
                      {program.programType === 3 &&
                        <Typography variant="body2">                            
                          Purchase credits up front at a discount.
                        </Typography>
                      }
                      {program.programType === 4 &&
                        <Typography variant="body2">                            
                          At the end of the month, a sales commission is calculated based on volume imported from an external system, and paid out as a rebate.
                        </Typography>
                      }
                      </Grid>
                    </Grid>
                  </Grid>

                  {/*<Grid item xs={12}>
                    <Grid container spacing={0} direction="column" justifyContent="flex-start">
                      <Grid item xs={12}> 
                        <Typography variant="h6">Commission incentives</Typography>
                      </Grid>
                      <Grid item xs={12}> 
                        {program.qualificationPeriod === 0 &&
                          <Typography variant="body2">
                              Commissons are based on last month sales
                          </Typography>
                        }
                        {program.qualificationPeriod === 1 &&
                          <Typography variant="body2">
                            Commissions are based on total cumulative sales
                          </Typography>
                        }
                      </Grid>
                    </Grid>
                  </Grid>*/}

                  <Grid item xs={12}>
                    <TableContainer>
                      <Table>
                        <TableHead className={classes.tblHead}>
                          <TableRow>
                            <TableCell align="right">Minimum sales</TableCell>
                            <TableCell align="right">Incentive percentage</TableCell>
                            {/*<TableCell align="center">{deleteHeader}</TableCell>*/}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            // this.state.programLines && this.state.programLines.map(line => {
                          programLines && programLines.map(line => {
                            let revenue = line.revenueMin;
                            let perc    = line.incentivePerc;

                            let revenueFormatted = Number(revenue)
                              .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                            return(
                              <TableRow key={line.resellerPlId}>
                                <TableCell align="right">
                                  {revenueFormatted}
                                    
                                </TableCell>
                                <TableCell align="right">
                                  {perc}
                                  
                                </TableCell>
                                
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              );
            }
            return <LoadingSpinner />
          }}
        />
      )
    } else {
      return (
        <Redirect to='/home' />
      );
    }
  }
}

RcpView.propTypes = {
    classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(RcpView);
